import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { UserSubscription, hash as hash2 } from "../gql/UserSubscription";
import { UserQuery, hash } from "../gql/UserQuery";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import Pagination from "react-bootstrap/Pagination";
import * as Dayjs from "dayjs";
import InputGroup from "react-bootstrap/InputGroup";
import ResetPassword from "./ResetPassword";
import AdminToggle from "./AdminToggle";
import ActiveToggle from "./ActiveToggle";
import AddUser from "./AddUser";
//import AssignProjects from "./AssignProjects";
import AssignDevices from "./AssignDevices";
import { FaUserTie } from "react-icons/fa";
import { FaLaptopHouse } from "react-icons/fa";
import AssignProjects from "./AssignProjects";

const UsersTable = ({ user }) => {
  const [search, setSearch] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [activePage, setActivePage] = useState(1);
  const { data, loading, error, subscribeToMore } = useQuery(UserQuery, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    context: { headers: { "X-Trust-Key": hash } },
  });

  subscribeToMore({
    document: UserSubscription,
    updateQuery: (prev) => {
      return prev;
    },
    context: { headers: { "X-Trust-Key": hash2 } },
  });

  const MAX_PAGES_DISPLAYED = 5;
  const indexOfLastItem = activePage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(
    data?.allUsers?.nodes
      ?.filter((u) => {
        if (showDisabled) {
          return u;
        }
        return u.isActive;
      })
      .filter((u) => {
        if (search.length > 0) {
          return u.email.toLowerCase().includes(search.toLowerCase());
        }
        return u;
      })?.length / itemsPerPage
  );

  const handlePaginationClick = (pageNumber) => {
    setActivePage(pageNumber);
  };

  const renderPaginationItems = () => {
    const items = [];
    const startPage = Math.max(
      1,
      activePage - Math.floor(MAX_PAGES_DISPLAYED / 2)
    );
    const endPage = Math.min(totalPages, startPage + MAX_PAGES_DISPLAYED - 1);
    if (startPage > 1) {
      items.push(
        <Pagination.Ellipsis
          key="ellipsis-start"
          onClick={() => handlePaginationClick(startPage - 1)}
        />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === activePage}
          onClick={() => handlePaginationClick(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      items.push(
        <Pagination.Ellipsis
          key="ellipsis-end"
          onClick={() => handlePaginationClick(endPage + 1)}
        />
      );
    }

    return items;
  };

  if (loading)
    return (
      <Spinner
        variant="primary"
        animation="grow"
        style={{ width: "400", height: "400" }}
      />
    );

  if (error) return <Alert>There was an issue with administration.</Alert>;
  if (data) {
    return (
      <Row>
        <Col md={9}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>Users</Card.Title>
              <InputGroup
                size="sm"
                style={{
                  width: "60%",
                }}
              >
                <InputGroup.Text>Search</InputGroup.Text>
                <Form.Control
                  size="sm"
                  type="search"
                  placeholder="User Search"
                  aria-label="search"
                  aria-describedby="user-search"
                  onChange={(e) => setSearch(e.target.value)}
                />
                <InputGroup.Text>
                  <Form.Check
                    size="sm"
                    type="switch"
                    aria-label="show-hide-disabled-users"
                    onChange={() => setShowDisabled(!showDisabled)}
                  />
                </InputGroup.Text>
                <InputGroup.Text>
                  {showDisabled ? "Hide Disabled Users" : "Show Disabled Users"}
                </InputGroup.Text>
              </InputGroup>
            </Card.Header>
            <Card.Body>
              <Table responsive="md" size="sm" striped hover>
                <thead>
                  <tr style={{ textAlign: "center" }}>
                    <th>Email</th>

                    <th className="d-none d-lg-table-cell">First Name</th>
                    <th className="d-none d-lg-table-cell">Last Name</th>
                    <th className="d-none d-lg-table-cell">Added On</th>
                    <th className="d-none d-lg-table-cell">Uploads</th>
                    <th>Admin</th>
                    <th>External</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.allUsers.nodes
                    .filter((u) => {
                      if (showDisabled) {
                        return u;
                      }
                      return u.isActive;
                    })
                    .filter((u) => {
                      if (search.length > 0) {
                        return u.email
                          .toLowerCase()
                          .includes(search.toLowerCase());
                      }
                      return u;
                    })
                    .slice(indexOfFirstItem, indexOfLastItem)
                    .map((users, idx) => (
                      <tr key={idx}>
                        <td
                          style={{
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <a
                            href={`mailto: ${users.email}`}
                            title={`Email ${users.firstName} ${users.lastName}`}
                          >
                            {users.email}
                          </a>
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {users.firstName}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {users.lastName}
                        </td>
                        <td className="d-none d-lg-table-cell">
                          {Dayjs(users.joinedOn).format("M/DD/YYYY")}
                        </td>
                        <td
                          style={{ textAlign: "center" }}
                          className="d-none d-lg-table-cell"
                        >
                          {users.uploadsByUserId.totalCount}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <FaUserTie
                            title="Admin"
                            style={{
                              color: "#f8991d",
                              display: users.isAdmin ? "inline-block" : "none",
                            }}
                          />
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <FaLaptopHouse
                            title="External"
                            style={{
                              color: "#f8991d",
                              display: users.isExternal
                                ? "inline-block"
                                : "none",
                            }}
                          />
                        </td>
                        <td>
                          <InputGroup>
                            <ResetPassword user={users} />
                            <AdminToggle user={users} cuser={user} />
                            <ActiveToggle user={users} cuser={user} />
                            <AssignProjects user={users} />
                            <AssignDevices user={users} />
                          </InputGroup>
                        </td>
                      </tr>
                    ))}
                  <tr>
                    <th
                      colSpan={10}
                      rowSpan={1}
                      style={{
                        fontWeight: "normal",
                        paddingTop: 0,
                        paddingBottom: 0,
                        margin: 0,
                      }}
                    >
                      <Pagination
                        size="sm"
                        className="m-1"
                        style={{ float: "right" }}
                      >
                        <Form.Select
                          size="sm"
                          title={`Show ${itemsPerPage} per page`}
                          style={{
                            maxWidth: "140px",
                            borderRight: 0,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                          onChange={(e) => {
                            setItemsPerPage(e.target.value);
                          }}
                        >
                          <option value={10}>Items/Page</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </Form.Select>
                        <Pagination.First
                          onClick={() => handlePaginationClick(1)}
                        />
                        <Pagination.Prev
                          onClick={() =>
                            handlePaginationClick(
                              activePage > 1 ? activePage - 1 : 1
                            )
                          }
                        />
                        {renderPaginationItems()}
                        <Pagination.Next
                          onClick={() =>
                            handlePaginationClick(
                              activePage < totalPages
                                ? activePage + 1
                                : totalPages
                            )
                          }
                        />
                        <Pagination.Last
                          onClick={() => handlePaginationClick(totalPages)}
                        />
                      </Pagination>
                    </th>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <AddUser />
        </Col>
      </Row>
    );
  }
};

export default UsersTable;
